import React from 'react';
import { useClient } from '../../context/ClientContext';
import KPICard from '../shared/KPICard';
import BudgetPacing from '../simple/BudgetPacing';
import MonthEndForecast from '../simple/MonthEndForecast';
import GoalPacing from '../simple/GoalPacing';
import ImpressionShare from '../simple/ImpressionShare';
import DailyCostTrend from '../simple/DailyCostTrend';
import CampaignTypeBreakdown from '../simple/CampaignTypeBreakdown';
import AccountHealth from '../simple/AccountHealth';
import EnquiriesTrend from '../simple/EnquiriesTrend';
import ManagerNotes from '../simple/ManagerNotes';

export default function OverviewPage({ summary, dailyTrend, refreshKey, dateRange }) {
  const { dataSources } = useClient();
  const features = dataSources.features || {};

  if (!summary) return null;

  const comparisonLabel = summary.comparisonLabel || 'vs last month';

  // Derive sparkline arrays from dailyTrend
  const days = dailyTrend?.days || [];
  const sparkEnquiries = days.length > 1 ? days.map(d => d.conversions) : null;
  const sparkSpend = days.length > 1 ? days.map(d => d.cost) : null;
  const sparkCPE = days.length > 1
    ? days.map(d => d.conversions > 0 ? d.cost / d.conversions : 0)
    : null;
  const sparkCTR = days.length > 1
    ? days.map(d => d.impressions > 0 ? (d.clicks / d.impressions) * 100 : 0)
    : null;

  return (
    <div className="space-y-6">
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            label="Enquiries"
            value={summary.enquiries}
            format="number"
            previousValue={summary.previousEnquiries}
            comparisonLabel={comparisonLabel}
            sparklineData={sparkEnquiries}
            brandSplit={summary.brand ? { brand: summary.brand.enquiries, nonBrand: summary.nonBrand.enquiries } : null}
          />
          <KPICard
            label="Cost per Enquiry"
            value={summary.costPerEnquiry}
            format="currency"
            previousValue={summary.previousCostPerEnquiry}
            comparisonLabel={comparisonLabel}
            sparklineData={sparkCPE}
            brandSplit={summary.brand ? { brand: summary.brand.costPerEnquiry, nonBrand: summary.nonBrand.costPerEnquiry } : null}
          />
          <KPICard
            label="Total Spend"
            value={summary.cost}
            format="currency"
            previousValue={summary.previousCost}
            comparisonLabel={comparisonLabel}
            sparklineData={sparkSpend}
            brandSplit={summary.brand ? { brand: summary.brand.cost, nonBrand: summary.nonBrand.cost } : null}
          />
          <KPICard
            label="CTR"
            value={summary.ctr}
            format="percent"
            previousValue={summary.previousCtr}
            comparisonLabel={comparisonLabel}
            sparklineData={sparkCTR}
            brandSplit={summary.brand ? { brand: summary.brand.ctr, nonBrand: summary.nonBrand.ctr } : null}
          />
        </div>
      </div>

      <BudgetPacing key={`budget-${refreshKey}`} />

      <GoalPacing dailyTrend={dailyTrend} summary={summary} dateRange={dateRange} />

      <MonthEndForecast summary={summary} dateRange={dateRange} />

      <ImpressionShare key={`is-${refreshKey}`} dateRange={dateRange} />

      <DailyCostTrend key={`daily-${refreshKey}`} dateRange={dateRange} />

      <CampaignTypeBreakdown key={`ctype-${refreshKey}`} dateRange={dateRange} />

      <AccountHealth key={`health-${refreshKey}`} dateRange={dateRange} />

      {features.enquiriesTrend !== false && (
        <EnquiriesTrend key={`trend-${refreshKey}`} />
      )}

      {features.managerNotes !== false && (
        <ManagerNotes key={`notes-${refreshKey}`} />
      )}
    </div>
  );
}
